import React from 'react';
import {
  AlefLogoMini,
  AlefLogo,
  NavigationMenu as NavMenu,
  Tag,
  TagText,
  Text,
} from '@alefeducation/ui-elements-library-core';
import {
  AngleLeft,
  AngleRight,
  FileLinesSolid,
  Progress,
  Reading,
  School,
  UserFriends,
  UsersGearSolid,
  UserTie,
} from '@alefeducation/ui-elements-library-icons';
import styled from 'styled-components';
import NavMenuItem from './NavMenuItemWrapper';
import {
  ROUTE_RNP,
  ROUTE_AVATAR_UPLOAD,
  PRODUCT_COURSE_MAPPING,
  ESIS_JOBS,
} from '../Routing/routeHelpers';
import {
  ADMINS,
  ROLES_AND_PERMISSIONS,
  NAV_ALEF_CONSOLE,
  NAV_ALL_USERS,
  NAV_COLLAPSE_MENU,
  NAV_COURSE_MAPPING,
  NAV_ESIS_LOGS,
  NAV_EXPAND_MENU,
  NAV_ORGANIZATIONS,
  NAV_SECTION_ACCESS,
  NAV_SECTION_PLATFORM,
  NAV_SECTION_SYSTEM,
  NAV_STUDENT_AVATARS,
} from '../../utils/constants';
import { FEATURE_TOGGLE } from '../../utils/FeatureToggle';

export const NAV_MENU_EXPANDED_WIDTH = 237;
export const NAV_MENU_COLLAPSED_WIDTH = 90;

interface CollapsibleProps {
  isCollapsed: boolean;
}

const StyledAlefLogoSection = styled.div`
  width: 64px;
  height: 32px;

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

const StyledNavMenuContainer = styled.nav<CollapsibleProps>`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  box-sizing: border-box;
  width: ${({ isCollapsed }) =>
    isCollapsed ? NAV_MENU_COLLAPSED_WIDTH : NAV_MENU_EXPANDED_WIDTH}px;
  border-right: 1px solid ${({ theme }: any) => theme.colors.primary['20']};
  transition: width 0.2s ease-in-out;

  > div {
    overflow-x: hidden;
  }

  .__react_component_tooltip.place-right {
    margin-left: -16px !important;
    &::after {
      margin-left: 0 !important;
    }
  }

  .nav-menu-item {
    position: relative;
    box-sizing: border-box;
    height: 40px;
    align-items: center;
    padding: 0 16px;
    border-radius: ${({ theme }: any) => theme.radii.default}px;
    width: ${({ isCollapsed }) => (isCollapsed ? '48px' : 'auto')};

    &::before {
      position: absolute;
      top: 8px;
      left: -2px;
      width: 3px;
      height: 24px;
      margin: 0;
      border-radius: 20px;
    }

    > div {
      color: ${({ theme }: any) => theme.colors.primary['100']};
      white-space: nowrap;
    }

    &:hover > div {
      color: ${({ theme }: any) => theme.colors.interactive['160']};
    }

    .nav-icon {
      margin-right: ${({ isCollapsed }) => (isCollapsed ? 0 : 16)}px;
    }
  }
`;

const StyledMenuContent = styled.div<CollapsibleProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ isCollapsed }) => (isCollapsed ? 16 : 0)}px;
  min-height: 100%;
`;

const StyledHeader = styled.div<CollapsibleProps>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 26px;
  box-sizing: border-box;
  height: 57px;
  padding: 0 ${({ isCollapsed }) => (isCollapsed ? 32 : 24)}px;
  border-bottom: 1px solid ${({ theme }: any) => theme.colors.primary['20']};

  > a {
    display: flex;
  }
`;

const StyledSections = styled.div<CollapsibleProps>`
  display: flex;
  flex-direction: column;
  gap: ${({ isCollapsed }) => (isCollapsed ? 16 : 0)}px;
  padding: 0 ${({ isCollapsed }) => (isCollapsed ? 0 : 16)}px;
`;

const StyledSectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 24px 0 16px;

  > span {
    padding-left: 24px;
  }
`;

const StyledItems = styled.div<CollapsibleProps>`
  display: flex;
  flex-direction: column;
  padding-left: ${({ isCollapsed }) => (isCollapsed ? 16 : 0)}px;
`;

const StyledSeparator = styled.div`
  height: 1px;
  background-color: ${({ theme }: any) => theme.colors.primary['20']};
`;

const StyledIconBox = styled.span.attrs({ className: 'nav-icon' })`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  width: 16px;
  height: 16px;

  svg {
    margin: 0 !important;
  }

  path[fill='#485465'],
  path[fill='#21BCB6'] {
    fill: currentColor;
  }
`;

const StyledWideIconBox = styled(StyledIconBox)`
  svg {
    position: absolute;
    left: -11.11%;
  }
`;

const StyledToggleButton = styled.button`
  position: absolute;
  top: 117px;
  right: -14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid ${({ theme }: any) => theme.colors.primary['40']};
  border-radius: 50%;
  background-color: ${({ theme }: any) => theme.colors.primary['0']};
  color: ${({ theme }: any) => theme.colors.primary['100']};
  cursor: pointer;
`;

interface NavItemConfig {
  to: string;
  label: string;
  icon: React.ReactElement;
  testId: string;
  isHomeRoute?: boolean;
  className?: string;
  isEnabled?: boolean;
}

interface NavSectionConfig {
  title: string;
  items: NavItemConfig[];
}

const getIcon = (Icon: React.ElementType) => (
  <StyledIconBox>
    <Icon width={16} height={16} />
  </StyledIconBox>
);

const navSections: NavSectionConfig[] = [
  {
    title: NAV_SECTION_PLATFORM,
    items: [
      {
        to: '/organisations',
        label: NAV_ORGANIZATIONS,
        icon: getIcon(School),
        testId: 'nav-item-organisations',
        isHomeRoute: true,
        className: 'item-org',
      },
      {
        to: PRODUCT_COURSE_MAPPING.productCourseMapping,
        label: NAV_COURSE_MAPPING,
        icon: getIcon(Progress),
        testId: 'nav-item-bulk-upload',
      },
    ],
  },
  {
    title: NAV_SECTION_ACCESS,
    items: [
      {
        to: '/members',
        label: NAV_ALL_USERS,
        icon: getIcon(UserFriends),
        testId: 'nav-item-members',
      },
      {
        to: ROUTE_RNP.rolesNPermissions,
        label: ROLES_AND_PERMISSIONS,
        icon: getIcon(UsersGearSolid),
        testId: 'nav-item-roles-permissions',
      },
      {
        to: '/admin-users',
        label: ADMINS,
        icon: getIcon(UserTie),
        testId: 'nav-item-admin',
        isEnabled: FEATURE_TOGGLE.ADMINS_TOGGLE,
      },
    ],
  },
  {
    title: NAV_SECTION_SYSTEM,
    items: [
      {
        to: ESIS_JOBS.esisLogs,
        label: NAV_ESIS_LOGS,
        icon: getIcon(FileLinesSolid),
        testId: 'nav-item-esis-logs',
      },
      {
        to: ROUTE_AVATAR_UPLOAD.avatarUpload,
        label: NAV_STUDENT_AVATARS,
        icon: getIcon(Reading),
        testId: 'nav-item-avatar-upload',
        isEnabled: FEATURE_TOGGLE.ENABLE_AVATAR_UPLOAD,
      },
    ],
  },
];

interface NavigationMenuProps {
  isCollapsed?: boolean;
  onToggle?(): void;
}

function NavigationMenu({
  isCollapsed = false,
  onToggle = () => {},
}: NavigationMenuProps) {
  return (
    <StyledNavMenuContainer
      data-testid="navigation-menu-container"
      isCollapsed={isCollapsed}
    >
      <NavMenu>
        <StyledMenuContent isCollapsed={isCollapsed}>
          <StyledHeader data-testid="navMenuHeader" isCollapsed={isCollapsed}>
            {isCollapsed ? (
              <AlefLogoMini href="/" />
            ) : (
              <>
                <StyledAlefLogoSection>
                  <a href="/">
                    <AlefLogo />
                  </a>
                </StyledAlefLogoSection>
                <Tag color="iceBlue" padding="medium">
                  <TagText>{NAV_ALEF_CONSOLE}</TagText>
                </Tag>
              </>
            )}
          </StyledHeader>

          <StyledSections isCollapsed={isCollapsed}>
            {navSections.map(({ title, items }) => (
              <React.Fragment key={title}>
                {!isCollapsed && (
                  <StyledSectionHeader>
                    <Text
                      variant="body-medium"
                      weight="light"
                      color="primary.60"
                    >
                      {title}
                    </Text>
                    <StyledSeparator />
                  </StyledSectionHeader>
                )}
                <StyledItems isCollapsed={isCollapsed}>
                  {items
                    .filter(({ isEnabled = true }) => isEnabled)
                    .map(
                      ({ to, label, icon, testId, isHomeRoute, className }) => (
                        <NavMenuItem
                          key={testId}
                          to={to}
                          icon={icon}
                          isCollapsed={isCollapsed}
                          isHomeRoute={isHomeRoute}
                          className={className}
                          data-testid={testId}
                        >
                          {label}
                        </NavMenuItem>
                      ),
                    )}
                </StyledItems>
                {isCollapsed && <StyledSeparator />}
              </React.Fragment>
            ))}
          </StyledSections>
        </StyledMenuContent>
      </NavMenu>

      <StyledToggleButton
        type="button"
        data-testid="nav-menu-toggle"
        aria-label={isCollapsed ? NAV_EXPAND_MENU : NAV_COLLAPSE_MENU}
        aria-expanded={!isCollapsed}
        onClick={onToggle}
      >
        {isCollapsed ? (
          <AngleRight cursor="pointer" width={10} height={10} />
        ) : (
          <AngleLeft width={10} height={10} />
        )}
      </StyledToggleButton>
    </StyledNavMenuContainer>
  );
}

export default NavigationMenu;
